/**
import { apiClient } from '../../utils/apiHelper';
 * 🔒 Fees Report - LOCKED SYSTEM
 * 
 * ⚠️ CRITICAL WARNING: CONTAINS LOCKED CALCULATIONS
 * ====================================================
 * DO NOT MODIFY WITHOUT EXPLICIT USER APPROVAL
 * 
 * LOCKED FEATURES:
 * - Discount & Scholarship columns display
 * - Outstanding balance calculation (includes discount/scholarship)
 * - Receipt generation logic
 * 
 * 📋 Documentation: See /app/LOCKED_SYSTEM.md
 * 🔒 Status: PERMANENTLY LOCKED (2025-12-08)
 */
import React, { useState, useEffect } from 'react';
import { Download, Calendar, DollarSign, TrendingUp, Printer, Search } from 'lucide-react';
import axios from 'axios';
import { generateFeePaymentReceipt } from '@/utils/feePaymentReceiptGenerator';
import Pagination from '@/components/Pagination';
import { formatDate } from '../../utils/dateTimeUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;
// SECURITY FIX: Read token at request time (not module load) to avoid stale
// token bleeding across tenant logins which broke tenant isolation.
const getToken = () => localStorage.getItem('token');

const FeesReport = () => {
  const [payments, setPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [filters, setFilters] = useState({ from_date: '', to_date: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ total: 0, count: 0 });
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalItems, setTotalItems] = useState(0);

  

  useEffect(() => {
    fetchPayments();
    loadCollegeInfo();
  }, [currentPage, itemsPerPage]);

  const loadCollegeInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
          logo_url: response.data.logo || response.data.logo_url
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      
      const response = await axios.get(`${API_URL}/api/fees/payments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      // Handle both old (array) and new (paginated) response
      let paymentsData = [];
      let total = 0;
      let statsData = { total: 0, count: 0 };
      
      if (Array.isArray(response.data)) {
        paymentsData = response.data;
        total = response.data.length;
        statsData = {
          total: response.data.reduce((sum, p) => sum + (p.amount || 0), 0),
          count: response.data.length
        };
      } else {
        paymentsData = response.data?.data || [];
        total = response.data?.total || 0;
        statsData = {
          total: response.data?.stats?.total_amount || 0,
          count: response.data?.stats?.total_count || 0
        };
      }
      
      // Group payments by student and mark first occurrence
      const processedPayments = [];
      const studentSeen = new Set();
      
      paymentsData.forEach(payment => {
        const isFirstForStudent = !studentSeen.has(payment.student_id);
        processedPayments.push({
          ...payment,
          show_one_time_fees: isFirstForStudent
        });
        studentSeen.add(payment.student_id);
      });
      
      setPayments(processedPayments);
      setAllPayments(processedPayments);
      setTotalItems(total);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFilterApply = () => {
    setCurrentPage(1);
    fetchPayments();
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setPayments(allPayments);
      return;
    }

    const filtered = allPayments.filter(payment => 
      payment.student_name?.toLowerCase().includes(term.toLowerCase()) ||
      payment.admission_number?.toLowerCase().includes(term.toLowerCase()) ||
      payment.course?.toLowerCase().includes(term.toLowerCase())
    );
    setPayments(filtered);
  };

  // ============================================================================
  // CRITICAL: RECEIPT GENERATION - DO NOT MODIFY FIELD NAMES
  // Fee payment records use: amount_paid, course_name, admission_number
  // DO NOT change to: amount, course, admission_no
  // ============================================================================
  const handlePrintReceipt = async (payment) => {
    try {
      // Fetch complete student data to get all fee information
      const studentResponse = await axios.get(`${API_URL}/api/students/${payment.student_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const student = studentResponse.data;
      
      // Fetch all payments for this student to calculate previously paid amount
      const paymentsResponse = await axios.get(`${API_URL}/api/fees/payments?student_id=${payment.student_id}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const rawPayments = paymentsResponse.data;
      const allPayments = Array.isArray(rawPayments) ? rawPayments : (rawPayments?.data || []);
      
      // Filter only installment/fee payments (exclude exam fees and other non-course payments)
      const feePayments = allPayments.filter(p => {
        const paymentFor = (p.payment_for || '').toLowerCase();
        return paymentFor.includes('installment') || paymentFor.includes('course fee') || paymentFor.includes('fee payment');
      });
      
      // Calculate total installment payments BEFORE this payment
      const paymentsBeforeThis = feePayments.filter(p => 
        new Date(p.payment_date) < new Date(payment.payment_date) ||
        (new Date(p.payment_date).getTime() === new Date(payment.payment_date).getTime() && p.id !== payment.id)
      );
      const totalInstallmentsPaid = paymentsBeforeThis.reduce((sum, p) => sum + (p.amount_paid || p.amount || 0), 0);

      // Bug fix (Jun 2026): when printing an admission/booking receipt, the CURRENT
      // payment IS the admission_fee / seat_booking_amount on the student record —
      // so it must NOT also be counted in "previouslyPaid", otherwise the receipt
      // double-counts ₹X and the Outstanding Balance is short by ₹X.
      const paymentForLower = (payment.payment_for || payment.payment_type || '').toLowerCase();
      const receiptUpper = String(payment.receipt_no || payment.receipt_number || '').toUpperCase();
      const isAdmissionReceipt = paymentForLower.includes('admission') || receiptUpper.startsWith('ADM');
      const isBookingReceipt = paymentForLower.includes('booking') || receiptUpper.startsWith('SB');

      let bookingAndAdmission;
      if (isBookingReceipt) {
        // Current payment IS the seat booking — neither booking nor admission counted as prior
        bookingAndAdmission = 0;
      } else if (isAdmissionReceipt) {
        // Current payment IS the admission fee — only seat-booking counts as prior
        bookingAndAdmission = (student.seat_booking_amount || 0);
      } else {
        // Course-fee / installment receipt — both booking + admission already paid earlier
        bookingAndAdmission = (student.seat_booking_amount || 0) + (student.admission_fee || 0);
      }
      const previouslyPaid = bookingAndAdmission + totalInstallmentsPaid;
      const currentPaymentAmount = payment.amount_paid || payment.amount || 0;
      const totalPaidAfter = previouslyPaid + currentPaymentAmount;
      
      // ============================================================================
      // CRITICAL: LOCKED FORMULA - DO NOT CHANGE
      // Outstanding Balance = Course Fee - Discount - Scholarship - Total Paid
      // ============================================================================
      const outstandingBalance = (student.total_course_fee || 0) 
        - (student.discount_amount || 0) 
        - (student.scholarship_amount || 0) 
        - totalPaidAfter;
      
      const receiptData = {
        student_name: payment.student_name || 'N/A',
        admission_no: payment.admission_number || payment.admission_no || 'N/A',
        course_name: payment.course_name || payment.course || 'N/A',
        session: payment.session || 'N/A',
        amount_paid: payment.amount_paid || payment.amount || 0,
        payment_date: payment.payment_date || new Date().toISOString(),
        receipt_no: payment.receipt_number || payment.receipt_no || 'N/A',
        payment_modes: payment.payment_modes && payment.payment_modes.length > 0 
          ? payment.payment_modes 
          : (payment.payment_mode ? [{ mode: payment.payment_mode, amount: payment.amount_paid || payment.amount }] : []),
        payment_for: payment.payment_for || 'Course Fee',
        remarks: payment.remarks || '',
        collected_by: 'Admin',
        // Accurate fee calculations
        total_fee: student.total_course_fee || 0,
        discount_amount: student.discount_amount || 0,
        scholarship_amount: student.scholarship_amount || 0,
        previously_paid: previouslyPaid,
        total_paid_after: totalPaidAfter,
        outstanding_balance: outstandingBalance
      };
      await generateFeePaymentReceipt(receiptData, collegeInfo);
    } catch (error) {
      console.error('Error generating receipt:', error);
      // Fallback: generate receipt with available data
      const receiptData = {
        student_name: payment.student_name || 'N/A',
        admission_no: payment.admission_number || payment.admission_no || 'N/A',
        course_name: payment.course_name || payment.course || 'N/A',
        session: payment.session || 'N/A',
        amount_paid: payment.amount_paid || payment.amount || 0,
        payment_date: payment.payment_date || new Date().toISOString(),
        receipt_no: payment.receipt_number || payment.receipt_no || 'N/A',
        payment_modes: payment.payment_modes && payment.payment_modes.length > 0 
          ? payment.payment_modes 
          : (payment.payment_mode ? [{ mode: payment.payment_mode, amount: payment.amount_paid || payment.amount }] : []),
        payment_for: payment.payment_for || 'Course Fee',
        remarks: payment.remarks || '',
        collected_by: payment.collected_by || 'Admin',
        discount_amount: payment.discount_amount || 0,
        scholarship_amount: payment.scholarship_amount || 0,
        total_fee: payment.total_fee || 0,
        previously_paid: payment.previously_paid || 0,
        outstanding_balance: payment.outstanding_balance || 0
      };
      await generateFeePaymentReceipt(receiptData, collegeInfo);
    }
  };

  // Export current filtered fee payments as CSV download.
  // Fetches ALL pages of the current date filter so the export is complete
  // (the visible table is paginated to 25/page by default).
  const [exporting, setExporting] = useState(false);
  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      params.append('page', 1);
      params.append('limit', 5000); // sweep all rows within the date range

      const resp = await axios.get(`${API_URL}/api/fees/payments?${params.toString()}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const rows = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
      if (rows.length === 0) {
        alert('No payments to export for the selected period.');
        return;
      }

      const csvEscape = (v) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const headers = [
        'Sl', 'Student Name', 'Admission Number', 'Course', 'Session',
        'Seat Booking', 'Admission Fee', 'Discount', 'Scholarship',
        'Installment Paid', 'Pay Date', 'Receipt Number', 'Payment Mode'
      ];
      const lines = [headers.join(',')];

      const seenStudent = new Set();
      rows.forEach((r, idx) => {
        const isFirstForStudent = !seenStudent.has(r.student_id);
        seenStudent.add(r.student_id);
        const modeDisplay = (r.payment_modes && r.payment_modes.length > 0)
          ? r.payment_modes.map(pm => `${pm.mode}:${pm.amount}`).join(' | ')
          : (r.payment_mode || 'N/A');
        const row = [
          idx + 1,
          r.student_name || 'N/A',
          r.admission_number || 'N/A',
          r.course || 'N/A',
          r.session || 'N/A',
          isFirstForStudent ? (r.seat_booking_amount || 0) : '',
          isFirstForStudent ? (r.admission_fee_amount || 0) : '',
          isFirstForStudent ? (r.discount_amount || 0) : '',
          isFirstForStudent ? (r.scholarship_amount || 0) : '',
          r.amount || 0,
          r.payment_date ? formatDate(r.payment_date) : 'N/A',
          r.receipt_number || 'N/A',
          modeDisplay
        ];
        lines.push(row.map(csvEscape).join(','));
      });

      const csvContent = lines.join('\n');
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const datePart = (filters.from_date && filters.to_date)
        ? `_${filters.from_date}_to_${filters.to_date}`
        : `_${new Date().toISOString().split('T')[0]}`;
      link.setAttribute('download', `fees_report${datePart}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export CSV failed:', err);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fees Report</h1>
          <p className="text-sm text-gray-500 mt-1">Comprehensive fee collection reports</p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={exporting}
          data-testid="fees-report-export-csv-btn"
          className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Collection</p>
              <p className="text-3xl font-bold text-green-600 mt-1">₹{stats.total.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Payments</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{stats.count}</p>
            </div>
            <TrendingUp className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Average Payment</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">₹{stats.count > 0 ? Math.round(stats.total / stats.count) : 0}</p>
            </div>
            <Calendar className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by student name, admission number, or course..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={filters.from_date}
                onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                className="border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={filters.to_date}
                onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                className="border rounded px-3 py-2"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleFilterApply}
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Apply Filter
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Number</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Seat Booking</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission Fee</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scholarship</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Installment Paid</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pay Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt Number</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Print Receipt</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="13" className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : payments.length === 0 ? (
              <tr><td colSpan="13" className="px-6 py-8 text-center text-gray-500">No payments found</td></tr>
            ) : (
              payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 text-sm font-medium text-gray-900">{payment.student_name || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm text-gray-700">{payment.admission_number || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{payment.course || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm text-gray-600">{payment.session || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-blue-600">
                    {payment.show_one_time_fees ? `₹${(payment.seat_booking_amount || 0).toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-purple-600">
                    {payment.show_one_time_fees ? `₹${(payment.admission_fee_amount || 0).toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-orange-600">
                    {payment.show_one_time_fees ? `₹${(payment.discount_amount || 0).toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-teal-600">
                    {payment.show_one_time_fees ? `₹${(payment.scholarship_amount || 0).toLocaleString('en-IN')}` : '-'}
                  </td>
                  <td className="px-4 py-4 text-sm font-semibold text-green-600">₹{(payment.amount || 0).toLocaleString('en-IN')}</td>
                  <td className="px-4 py-4 text-sm text-gray-500">{payment.payment_date ? formatDate(payment.payment_date) : 'N/A'}</td>
                  <td className="px-4 py-4 text-sm font-medium text-blue-600">{payment.receipt_number || 'N/A'}</td>
                  <td className="px-4 py-4 text-sm">
                    {payment.payment_modes && payment.payment_modes.length > 0 ? (
                      <div className="space-y-1">
                        {payment.payment_modes.map((pm, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded font-semibold">
                              {pm.mode}
                            </span>
                            <span className="text-gray-600 text-xs">
                              ₹{(pm.amount || 0).toLocaleString('en-IN')}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-600">{payment.payment_mode || 'N/A'}</span>
                    )}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handlePrintReceipt(payment)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      title="Print Receipt"
                    >
                      <Printer size={16} />
                      Print
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalItems / itemsPerPage)}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
};

export default FeesReport;